import { Alert, Modal, Select, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const { Text } = Typography

type AvailableRole = { id: number; name: string }

type ArchiveRoleModalProps = {
  open: boolean
  roleName: string
  memberCount: number
  availableRoles: AvailableRole[]
  confirmLoading?: boolean
  onConfirm: (replacementRoleId: number) => void
  /** Taken when nobody holds the role: there is nothing to reassign. */
  onDeleteOnly?: () => void
  onCancel: () => void
}

export const ArchiveRoleModal = ({
  open,
  roleName,
  memberCount,
  availableRoles,
  confirmLoading,
  onConfirm,
  onDeleteOnly,
  onCancel
}: ArchiveRoleModalProps) => {
  const { t } = useTranslation()
  const [replacement, setReplacement] = useState<number | undefined>(undefined)

  // A role nobody holds has nothing to reassign, so the picker would be an
  // empty step in front of a plain delete.
  const isUnheld = memberCount === 0 && onDeleteOnly != null

  const replacementLabel = useMemo(() => {
    const found = availableRoles.find((r) => r.id === replacement)

    return found?.name ?? ''
  }, [availableRoles, replacement])

  const handleCancel = () => {
    setReplacement(undefined)
    onCancel()
  }

  const handleOk = () => {
    if (isUnheld) {
      onDeleteOnly()

      return
    }
    if (replacement != null) {
      onConfirm(replacement)
    }
  }

  return (
    <Modal
      open={open}
      title={t('workspace.orgRoles.archiveConfirmTitle', { name: roleName })}
      onCancel={handleCancel}
      onOk={handleOk}
      okText={
        isUnheld
          ? t('workspace.orgRoles.actions.delete')
          : t('workspace.orgRoles.archiveAndReassign')
      }
      okButtonProps={{
        disabled: !isUnheld && replacement == null,
        danger: isUnheld,
        loading: confirmLoading,
        style:
          !isUnheld && replacement != null
            ? { backgroundColor: '#fa8c16', borderColor: '#fa8c16' }
            : undefined
      }}
      destroyOnHidden
    >
      <Text>
        {isUnheld
          ? t('workspace.orgRoles.deleteConfirmDescription')
          : t('workspace.orgRoles.archiveConfirmDescription', {
              count: memberCount
            })}
      </Text>

      {isUnheld ? null : (
        <>
          <Select<number>
            style={{ width: '100%', marginTop: 12 }}
            placeholder={t('workspace.orgRoles.selectRole')}
            value={replacement}
            onChange={setReplacement}
            options={availableRoles.map((r) => ({
              value: r.id,
              label: r.name
            }))}
          />

          {replacement != null ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              title={t('workspace.orgRoles.archiveReassignWarning', {
                count: memberCount,
                role: replacementLabel
              })}
            />
          ) : null}
        </>
      )}
    </Modal>
  )
}
